// Layer: infrastructure. OIDC Authorization Code flow with PKCE.
//
// The console is a public client: it ships to a browser and can keep no secret. That
// rules out the client-credentials and implicit flows outright:
//
//   implicit    returns the token in the URL fragment, where it lands in browser
//               history, in any `Referer` the page later sends, and in the hands of
//               anything that can read `location`. It is deprecated for exactly this.
//   PKCE        sends only a hash of a per-attempt secret up front, and the secret
//               itself when redeeming the code. An attacker who intercepts the code
//               cannot redeem it without the verifier, which never left this tab.
//
// The verifier lives in sessionStorage, not localStorage: it is single-use and
// per-tab, and must not outlive the tab that created it. The access token stays in
// memory only. See token-source.ts.

const VERIFIER_KEY = "storia.pkce.verifier";
const STATE_KEY = "storia.pkce.state";

export interface OidcConfig {
  authorizeUrl: string;
  tokenUrl: string;
  clientId: string;
  redirectUri: string;
  scope?: string;
}

export interface TokenResponse {
  access_token: string;
  expires_in?: number;
  token_type?: string;
}

export class OidcError extends Error {}

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

export async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return base64Url(new Uint8Array(digest));
}

/** Build the authorization URL and stash the single-use verifier and state. */
export async function beginLogin(
  config: OidcConfig,
  storage: Storage = sessionStorage,
): Promise<string> {
  const verifier = randomString();
  const state = randomString(16);
  storage.setItem(VERIFIER_KEY, verifier);
  storage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scope ?? "openid profile",
    state,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: "S256",
  });
  return `${config.authorizeUrl}?${params.toString()}`;
}

/** Read `?code=...&state=...` from the redirect, if present. */
export function readCallback(
  location: Pick<Location, "search">,
): { code: string; state: string } | null {
  const params = new URLSearchParams(location.search);
  const code = params.get("code");
  const state = params.get("state");
  if (!code || !state) return null;
  return { code, state };
}

/**
 * Exchange the authorization code for a token.
 *
 * `state` is compared against the stashed value before anything else: without that
 * check an attacker can hand the victim a code of their own choosing and have the
 * console redeem it, logging the victim into the attacker's account. The verifier is
 * cleared whether or not the exchange succeeds, because it is single-use and a
 * lingering one is a replay waiting to happen.
 */
export async function completeLogin(
  config: OidcConfig,
  callback: { code: string; state: string },
  options: { storage?: Storage; fetchImpl?: typeof fetch } = {},
): Promise<TokenResponse> {
  const storage = options.storage ?? sessionStorage;
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);

  const verifier = storage.getItem(VERIFIER_KEY);
  const expectedState = storage.getItem(STATE_KEY);
  storage.removeItem(VERIFIER_KEY);
  storage.removeItem(STATE_KEY);

  if (!verifier || !expectedState) {
    throw new OidcError("no sign-in is in progress in this tab");
  }
  if (callback.state !== expectedState) {
    throw new OidcError("State mismatch. Refusing to complete sign-in.");
  }

  const response = await doFetch(config.tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code: callback.code,
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      code_verifier: verifier,
    }).toString(),
  });

  if (!response.ok) {
    throw new OidcError(`token exchange failed (${response.status})`);
  }
  const body = (await response.json()) as Partial<TokenResponse>;
  if (!body.access_token) {
    throw new OidcError("token response carried no access_token");
  }
  return body as TokenResponse;
}

/** Strip `code` and `state` from the URL so they do not survive in history. */
export function scrubCallback(location: Location, history: History): void {
  const params = new URLSearchParams(location.search);
  params.delete("code");
  params.delete("state");
  const query = params.toString();
  history.replaceState(null, "", `${location.pathname}${query ? `?${query}` : ""}`);
}
